import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { Clipboard } from '@angular/cdk/clipboard';
import { computed, inject, Signal } from '@angular/core';
import { Router } from '@angular/router';
import {
  Agreement,
  AgreementListItem,
  AgreementLogListItem,
  AgreementVersion,
  AgreementVersionListItem,
  CreateAgreementRequest,
  CreateVersionRequest,
  UpdateAgreementRequest,
  UpdateVersionRequest,
} from '@models/index';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { NotificationsService } from 'cui-components';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';
import { AppConfigService } from 'src/app/infrastructure';
import {
  addEntity,
  createCustomStateSetOnUrlHandler,
  createStateResetHandler,
  deleteEntity,
  findAndReplace,
  getAll,
  getOne,
  sortAlphabetically,
  StateDictionary,
  updateEntity,
} from '../helpers';
import { AgreementsService } from './agreements.service';

type AgreementsState = {
  agreements: AgreementListItem[];
  isLoading: boolean;
  agreementCreateLoading: boolean;
  selectedAgreement: Agreement | null;
  selectedAgreementLoading: boolean;

  agreementVersions: AgreementVersionListItem[];
  versionsLoading: boolean;
  versionCreateLoading: boolean;
  selectedVersion: AgreementVersion | null;
  selectedVersionLoading: boolean;

  agreementLogs: AgreementLogListItem[];
  logsLoading: boolean;
};

const initialState: AgreementsState = {
  agreements: [],
  isLoading: false,
  agreementCreateLoading: false,
  selectedAgreement: null,
  selectedAgreementLoading: false,

  agreementVersions: [],
  versionsLoading: false,
  versionCreateLoading: false,
  selectedVersion: null,
  selectedVersionLoading: false,

  agreementLogs: [],
  logsLoading: false,
};

const agreementsDictionary: StateDictionary<AgreementListItem> = {
  entityList: 'agreements',
  listLoading: 'isLoading',
  selectedEntity: 'selectedAgreement',
  selectedEntityLoading: 'selectedAgreementLoading',
  entityId: 'id',
  createLoading: 'agreementCreateLoading',
  sortFn: (a, b) => a.name.localeCompare(b.name),
};

export const AgreementsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('agreements-store'),

  withComputed(store => ({
    selectedAgreementId: computed(() => store.selectedAgreement()?.id || null),
    selectedVersionId: computed(() => store.selectedVersion()?.id || null),
    isAgreementSignedOnce: computed(() => !!store.agreementLogs().length),
  })),

  withMethods(
    (
      store,
      agreementsService = inject(AgreementsService),
      router = inject(Router),
      clipboard = inject(Clipboard),
      appConfig = inject(AppConfigService),
      notifications = inject(NotificationsService),
    ) => {
      createStateResetHandler<AgreementsState>('/agreements')(store, initialState);
      createCustomStateSetOnUrlHandler<AgreementsState>('/agreements', { selectedAgreement: null })(store);

      const agreementMethods = {
        getAll: getAll<AgreementListItem>({
          store,
          service: agreementsService,
          dictionary: agreementsDictionary,
        }),

        getOne: getOne<Agreement>({
          store,
          service: agreementsService,
          dictionary: agreementsDictionary,
        }),

        createAgreement: addEntity<Agreement, CreateAgreementRequest>({
          store,
          service: agreementsService,
          dictionary: agreementsDictionary,
          onSuccessCallback: (newAgreement: Agreement) => {
            patchState(store, { selectedAgreement: newAgreement });
            router.navigate(['agreements', newAgreement.id, 'details']);
          },
        }),

        updateAgreement: updateEntity<Agreement, UpdateAgreementRequest>({
          store,
          service: agreementsService,
          dictionary: agreementsDictionary,
          onSuccessCallback: (updatedAgreement: Agreement) => {
            patchState(store, { selectedAgreement: updatedAgreement });
          },
        }),

        deleteAgreement: deleteEntity<Agreement>({
          store,
          service: agreementsService,
          dictionary: agreementsDictionary,
          onSuccessCallback: () => {
            router.navigate(['agreements']);
          },
        }),

        getAgreementLogs: rxMethod<string>(
          pipe(
            tap(() => patchState(store, { logsLoading: true })),
            switchMap(agreementId =>
              agreementsService.getLogs(agreementId).pipe(
                tap(logs => {
                  patchState(store, {
                    agreementLogs: logs,
                    logsLoading: false,
                  });
                }),
                catchError(() => {
                  patchState(store, { logsLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        copySelectedAgreementPublicLink: (): void => {
          const agreement = store.selectedAgreement();
          if (agreement) {
            clipboard.copy(`${appConfig.config['FRONTEND_PORTAL_URL']}/agreements/${agreement.publicLink}`);
            notifications.showNotification('Copied successfully.');
          }
        },
      };

      const versionMethods = {
        getAllVersions: rxMethod<string>(
          pipe(
            tap(() => patchState(store, { versionsLoading: true })),
            switchMap(agreementId =>
              agreementsService.getVersions(agreementId).pipe(
                tap(versions => {
                  patchState(store, {
                    agreementVersions: sortAlphabetically([...versions], (a, b) => a.versionNo.localeCompare(b.versionNo)),
                    versionsLoading: false,
                  });
                }),
                catchError(() => {
                  patchState(store, { versionsLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        getOneVersion: rxMethod<{ agreementId: string; versionId: string }>(
          pipe(
            tap(() => patchState(store, { selectedVersionLoading: true })),
            switchMap(({ agreementId, versionId }) =>
              agreementsService.getAgreementVersion(agreementId, versionId).pipe(
                tap(version => {
                  patchState(store, {
                    selectedVersion: version,
                    selectedVersionLoading: false,
                  });
                }),
                catchError(() => {
                  patchState(store, { selectedVersionLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        addVersion: rxMethod<{ agreementId: string; version: CreateVersionRequest }>(
          pipe(
            tap(() => patchState(store, { versionCreateLoading: true, versionsLoading: true })),
            switchMap(({ agreementId, version }) =>
              agreementsService.addAgreementVersion(agreementId, version).pipe(
                tap(createdVersion => {
                  patchState(store, {
                    versionCreateLoading: false,
                    versionsLoading: false,
                    selectedVersion: createdVersion,
                    agreementVersions: sortAlphabetically([...store.agreementVersions(), createdVersion], (a, b) =>
                      a.versionNo.localeCompare(b.versionNo),
                    ),
                  });
                  router.navigate(['agreements', agreementId, 'versions', createdVersion.id, 'details']);
                }),
                catchError(() => {
                  patchState(store, { versionCreateLoading: false, versionsLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        updateVersion: rxMethod<{ agreementId: string; versionId: string; version: UpdateVersionRequest }>(
          pipe(
            tap(() => patchState(store, { selectedVersionLoading: true, versionsLoading: true })),
            switchMap(({ agreementId, versionId, version }) =>
              agreementsService.updateAgreementVersion(agreementId, versionId, version).pipe(
                tap(updatedVersion => {
                  patchState(store, {
                    versionsLoading: false,
                    selectedVersionLoading: false,
                    selectedVersion: updatedVersion,
                    agreementVersions: sortAlphabetically(
                      findAndReplace(store.agreementVersions(), updatedVersion, item => item.id === versionId),
                      (a, b) => a.versionNo.localeCompare(b.versionNo),
                    ),
                  });
                }),
                catchError(() => {
                  patchState(store, {
                    selectedVersion: store.selectedVersion(),
                    versionsLoading: false,
                    selectedVersionLoading: false,
                  });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        deleteVersion: rxMethod<{ agreementId: string; versionId: string }>(
          pipe(
            tap(() => patchState(store, { versionsLoading: true })),
            switchMap(({ agreementId, versionId }) =>
              agreementsService.deleteAgreementVersion(agreementId, versionId).pipe(
                tap(() => {
                  patchState(store, {
                    versionsLoading: false,
                    selectedVersion: null,
                  });
                  router.navigate(['agreements', agreementId, 'versions']);
                  versionMethods.getAllVersions(agreementId);
                }),
                catchError(() => {
                  patchState(store, { versionsLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        publishVersion: rxMethod<{ agreementId: string; versionId: string }>(
          pipe(
            tap(() => patchState(store, { selectedVersionLoading: true, versionsLoading: true })),
            switchMap(({ agreementId, versionId }) =>
              agreementsService.publishAgreementVersion(agreementId, versionId).pipe(
                tap(publishedVersion => {
                  patchState(store, {
                    versionsLoading: false,
                    selectedVersionLoading: false,
                    selectedVersion: publishedVersion,
                    agreementVersions: sortAlphabetically(
                      findAndReplace(store.agreementVersions(), publishedVersion, item => item.id === versionId),
                      (a, b) => a.versionNo.localeCompare(b.versionNo),
                    ),
                  });
                  versionMethods.getAllVersions(agreementId);
                }),
                catchError(() => {
                  patchState(store, {
                    selectedVersion: store.selectedVersion(),
                    versionsLoading: false,
                    selectedVersionLoading: false,
                  });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),
      };

      const navigationActions = {
        goToAgreementActions: async (id: string): Promise<boolean> => {
          const result = await router.navigate(['agreements', id]);
          if (!result) return false;

          patchState(store, { agreementVersions: [], agreementLogs: [], selectedVersion: null });
          if (!store.selectedAgreementLoading()) agreementMethods.getOne(id);
          return true;
        },

        goToListPage: async (): Promise<boolean> => {
          const result = await router.navigate(['agreements']);
          if (!result) return false;

          patchState(store, { selectedAgreement: null });
          return true;
        },

        goToVersionsList: async (agreementId: string): Promise<boolean> => {
          const result = await router.navigate(['agreements', agreementId, 'versions']);
          if (!result) return false;

          patchState(store, { selectedVersion: null });
          return true;
        },

        goToVersionActions: async (agreementId: string, versionId: string): Promise<boolean> => {
          const result = await router.navigate(['agreements', agreementId, 'versions', versionId]);
          if (!result) return false;

          if (!store.selectedVersionLoading()) versionMethods.getOneVersion({ agreementId, versionId });
          return true;
        },
      };

      return {
        ...agreementMethods,
        ...versionMethods,
        ...navigationActions,
      };
    },
  ),
);

export interface AgreementsStoreType {
  // State properties
  readonly agreements: Signal<AgreementListItem[]>;
  readonly isLoading: Signal<boolean>;
  readonly selectedAgreement: Signal<Agreement | null>;
  readonly selectedAgreementLoading: Signal<boolean>;
  readonly agreementCreateLoading: Signal<boolean>;

  readonly agreementVersions: Signal<AgreementVersionListItem[]>;
  readonly versionsLoading: Signal<boolean>;
  readonly versionCreateLoading: Signal<boolean>;
  readonly selectedVersion: Signal<AgreementVersion | null>;
  readonly selectedVersionLoading: Signal<boolean>;
  readonly agreementLogs: Signal<AgreementLogListItem[]>;
  readonly logsLoading: Signal<boolean>;

  readonly selectedAgreementId: Signal<string | null>;
  readonly selectedVersionId: Signal<string | null>;
  readonly isAgreementSignedOnce: Signal<boolean>;

  // Agreement methods
  readonly getAll: () => void;
  readonly getOne: (id: string) => void;
  readonly createAgreement: (agreement: CreateAgreementRequest) => void;
  readonly updateAgreement: (params: { id: string; dto: UpdateAgreementRequest }) => void;
  readonly deleteAgreement: (id: string) => void;
  readonly getAgreementLogs: (agreementId: string) => void;
  readonly copySelectedAgreementPublicLink: () => void;

  // Version methods
  readonly getAllVersions: (agreementId: string) => void;
  readonly getOneVersion: (params: { agreementId: string; versionId: string }) => void;
  readonly addVersion: (params: { agreementId: string; version: CreateVersionRequest }) => void;
  readonly updateVersion: (params: { agreementId: string; versionId: string; version: UpdateVersionRequest }) => void;
  readonly deleteVersion: (params: { agreementId: string; versionId: string }) => void;
  readonly publishVersion: (params: { agreementId: string; versionId: string }) => void;

  // Navigation methods
  readonly goToAgreementActions: (id: string) => Promise<boolean>;
  readonly goToListPage: () => Promise<boolean>;
  readonly goToVersionsList: (agreementId: string) => Promise<boolean>;
  readonly goToVersionActions: (agreementId: string, versionId: string) => Promise<boolean>;
}
